import { useCallback, useMemo, useSyncExternalStore } from "react";

const NAVIGATION_EVENT = "atomicpay:navigation";

function subscribe(listener) {
  window.addEventListener("popstate", listener);
  window.addEventListener(NAVIGATION_EVENT, listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener(NAVIGATION_EVENT, listener);
  };
}

function snapshot() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function useLocation() {
  useSyncExternalStore(subscribe, snapshot, () => "/");
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

export function useNavigate() {
  return useCallback((to, options = {}) => {
    window.history[options.replace ? "replaceState" : "pushState"]({}, "", to);
    window.dispatchEvent(new Event(NAVIGATION_EVENT));
  }, []);
}

export function useParams() {
  const { pathname } = useLocation();
  return useMemo(() => {
    const commerceMatch = pathname.match(/^\/commerce\/([^/]+)\/?$/);
    if (commerceMatch) {
      return { checkoutToken: decodeURIComponent(commerceMatch[1]) };
    }
    const checkoutMatch = pathname.match(/^\/([^/]+)\/?$/);
    return checkoutMatch
      ? { id: decodeURIComponent(checkoutMatch[1]) }
      : {};
  }, [pathname]);
}

export function useSearchParams() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const setParams = useCallback(
    (next, options) => {
      const value =
        typeof next === "function" ? next(new URLSearchParams(params)) : next;
      const query =
        value instanceof URLSearchParams
          ? value.toString()
          : new URLSearchParams(value).toString();
      navigate(`${location.pathname}${query ? `?${query}` : ""}`, options);
    },
    [location.pathname, navigate, params]
  );
  return [params, setParams];
}
